/**
 * product-amazon.js
 * — Gallery switching
 * — Real image zoom (lens + glass panel)
 * — Variant selection
 * — Qty stepper
 * — Add to Cart (selling_plan aware — passes ALL form fields)
 * — Buy Now (selling_plan aware)
 * — Mobile sticky bar on scroll
 * — WhatsApp URL update on variant change
 */
(function () {
  'use strict';

  document.querySelectorAll('.amz-page').forEach(page => {
    const sid      = page.dataset.sectionId;
    const pdata    = document.getElementById('amz-pdata-' + sid);
    if (!pdata) return;
    const product  = JSON.parse(pdata.textContent);
    const variants = product.variants;

    /* ── refs ── */
    const thumbs      = [...page.querySelectorAll('.amz-thumb')];
    const mediaItems  = [...page.querySelectorAll('.amz-media')];
    const variantInput= page.querySelector('#amz-vid-' + sid);
    const bbPrice     = page.querySelector('#amz-bb-price-' + sid);
    const col2Price   = page.querySelector('.amz-price__now');
    const waBtn       = page.querySelector('#amz-wa-' + sid);
    const mobileBar   = page.querySelector('#amz-mobile-bar-' + sid);
    const mobilePx    = page.querySelector('#amz-mobile-bar-price-' + sid);
    const buybox      = page.querySelector('#amz-buybox-' + sid);

    /* ════════════════════════════
       HELPER — collect ALL form fields including selling_plan
       This is the critical fix: FormData captures every <input> the
       subscription app injects (selling_plan, selling_plan_id, etc.)
       We then pass them all to /cart/add.js so Shopify applies the plan.
    ════════════════════════════ */
    function buildCartPayload(form) {
      const fd = new FormData(form);

      const payload = {
        id:       parseInt(fd.get('id'), 10),
        quantity: parseInt(fd.get('quantity'), 10) || 1,
      };

      // ── CRITICAL: include selling_plan if app injected it ──
      const sellingPlan = fd.get('selling_plan');
      if (sellingPlan && sellingPlan !== '' && sellingPlan !== '0') {
        payload.selling_plan = sellingPlan;
      }

      // Include any line item properties the app may have set
      const props = {};
      for (const [k, v] of fd.entries()) {
        if (k.startsWith('properties[')) {
          const name = k.replace('properties[', '').replace(']', '');
          props[name] = v;
        }
      }
      if (Object.keys(props).length > 0) {
        payload.properties = props;
      }

      return payload;
    }

    /* ════════════════════════════
       1. GALLERY — thumb click
    ════════════════════════════ */
    thumbs.forEach(btn => {
      btn.addEventListener('click', () => switchMedia(btn.dataset.mediaId));
    });

    function switchMedia(id) {
      thumbs.forEach(t => t.classList.toggle('is-active', t.dataset.mediaId === id));
      mediaItems.forEach(m => m.classList.toggle('is-visible', m.dataset.mediaId === id));
    }

    /* ════════════════════════════
       2. IMAGE ZOOM — lens + glass
    ════════════════════════════ */
    page.querySelectorAll('.amz-zoom').forEach(initZoom);

    function initZoom(wrap) {
      const img     = wrap.querySelector('.amz-zoom__img');
      const lens    = wrap.querySelector('.amz-zoom__lens');
      const glass   = wrap.querySelector('.amz-zoom__glass');
      const zoomSrc = wrap.dataset.zoom;

      if (!img || !lens || !glass || !zoomSrc) return;
      if (!window.matchMedia('(hover: hover)').matches) return;

      const LENS_W = 120, LENS_H = 120;
      lens.style.width  = LENS_W + 'px';
      lens.style.height = LENS_H + 'px';

      const hi = new Image();
      hi.src = zoomSrc;

      function getPos(e) {
        const r = img.getBoundingClientRect();
        let x = (e.clientX ?? 0) - r.left;
        let y = (e.clientY ?? 0) - r.top;
        x = Math.max(LENS_W / 2, Math.min(r.width  - LENS_W / 2, x));
        y = Math.max(LENS_H / 2, Math.min(r.height - LENS_H / 2, y));
        return { x, y, rw: r.width, rh: r.height };
      }

      function move(e) {
        const { x, y, rw, rh } = getPos(e);
        lens.style.left = (x - LENS_W / 2) + 'px';
        lens.style.top  = (y - LENS_H / 2) + 'px';

        const natW = hi.naturalWidth  || img.naturalWidth  || 1200;
        const natH = hi.naturalHeight || img.naturalHeight || 1200;
        const gw   = glass.offsetWidth  || 400;
        const gh   = glass.offsetHeight || 400;

        glass.style.backgroundImage    = `url('${zoomSrc}')`;
        glass.style.backgroundSize     = `${natW}px ${natH}px`;
        glass.style.backgroundPosition = `-${(x * (natW / rw) - gw / 2).toFixed(1)}px -${(y * (natH / rh) - gh / 2).toFixed(1)}px`;
      }

      wrap.addEventListener('mouseenter', () => { wrap.classList.add('is-zooming'); glass.style.backgroundImage = `url('${zoomSrc}')`; });
      wrap.addEventListener('mousemove',  move);
      wrap.addEventListener('mouseleave', () => wrap.classList.remove('is-zooming'));
    }

    /* ════════════════════════════
       3. VARIANT SELECTION
    ════════════════════════════ */
    function formatMoney(cents) {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(cents / 100);
    }

    function selectedOptions() {
      const opts = [];
      page.querySelectorAll('.amz-option').forEach(el => {
        const idx = parseInt(el.dataset.optionIndex, 10);
        const r   = el.querySelector('.amz-pill__r:checked, .amz-swatch__r:checked');
        if (r) opts[idx] = r.value;
      });
      return opts;
    }

    function findVariant(opts) {
      return variants.find(v => v.options.every((o, i) => o === opts[i]));
    }

    function applyVariant(v) {
      if (!v) return;

      // Update hidden variant id input
      if (variantInput) variantInput.value = v.id;

      // Update all [name="id"] inputs the app may have added
      const form = variantInput?.closest('form');
      if (form) {
        form.querySelectorAll('[name="id"]').forEach(inp => {
          inp.value = v.id;
          inp.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      // Update prices
      const priceStr = formatMoney(v.price);
      if (bbPrice)   bbPrice.textContent  = priceStr;
      if (col2Price) col2Price.textContent = priceStr;
      if (mobilePx)  mobilePx.textContent  = priceStr;

      // Stock badge
      const stockEl = page.querySelector('.amz-stock');
      if (stockEl) {
        stockEl.textContent = v.available ? '✓ In Stock' : '✗ Out of Stock';
        stockEl.className   = 'amz-stock amz-stock--' + (v.available ? 'in' : 'out');
      }

      // Enable/disable buttons
      page.querySelectorAll('.amz-btn--cart, .amz-btn--buynow, .amz-mobile-bar__btn--cart').forEach(b => {
        b.disabled = !v.available;
        b.setAttribute('aria-disabled', String(!v.available));
      });

      // WhatsApp link
      if (waBtn) {
        const num  = waBtn.href.match(/wa\.me\/([^?]+)/)?.[1] || '';
        waBtn.href = `https://wa.me/${num}?text=${encodeURIComponent(`${product.title} — ${v.title} — ${window.location.href}`)}`;
      }

      // Swap gallery to variant image
      if (v.featured_image) {
        const t = thumbs.find(b => b.dataset.mediaId === String(v.featured_image.id));
        if (t) t.click();
      }

      // Update URL
      history.replaceState({}, '', `?variant=${v.id}`);

      // ── Fire events so subscription/bundle apps react ──

      // 1. Shopify standard — Recharge, Skio, Bold, Loop all listen for this
      document.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant: v, form: form, product: product }
      }));

      // Also fire on the form element itself (some apps listen on the form)
      if (form) {
        form.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant: v, product: product }
        }));
      }

      // 2. jQuery fallback (older apps: Recharge v1, Bold v1)
      if (window.jQuery) {
        if (form) window.jQuery(form).trigger('change');
        window.jQuery(document).trigger('variant:change', [{ variant: v }]);
      }

      // 3. Native change event on the variant input (most reliable catch-all)
      if (variantInput) {
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    page.querySelectorAll('.amz-pill__r, .amz-swatch__r').forEach(r => {
      r.addEventListener('change', () => {
        const oi  = r.dataset.oi;
        const lbl = page.querySelector('.js-sel-' + oi);
        if (lbl) lbl.textContent = r.value;

        const grp = r.closest('.amz-pills, .amz-swatches');
        if (grp) {
          grp.querySelectorAll('.amz-pill, .amz-swatch').forEach(el => el.classList.remove('is-sel'));
          r.closest('.amz-pill, .amz-swatch')?.classList.add('is-sel');
        }

        const v = findVariant(selectedOptions());
        if (v) applyVariant(v);
      });
    });

    /* ════════════════════════════
       4. QTY STEPPER
    ════════════════════════════ */
    page.querySelectorAll('.amz-qty__btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const inp = btn.closest('.amz-qty').querySelector('.amz-qty__input');
        let val   = parseInt(inp.value, 10) || 1;
        const min = parseInt(inp.min, 10) || 1;
        const max = parseInt(inp.max, 10) || 999;
        inp.value = btn.dataset.dir === 'dec' ? Math.max(min, val - 1) : Math.min(max, val + 1);
      });
    });

    /* ════════════════════════════
       5. ADD TO CART
       Uses FormData so selling_plan and ALL app-injected
       fields are captured and sent to Shopify.
    ════════════════════════════ */
    page.querySelectorAll('.js-atc').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();

        const formId = btn.dataset.form || btn.closest('form')?.id;
        const form   = document.getElementById(formId);
        if (!form) return;

        const orig = btn.innerHTML;
        btn.innerHTML = '⏳ Adding…';
        btn.disabled  = true;

        try {
          const payload = buildCartPayload(form);

          const res = await fetch('/cart/add.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body:    JSON.stringify(payload)
          });

          if (!res.ok) {
            const err = await res.json();
            throw new Error(err.description || 'Add to cart failed');
          }

          const item = await res.json();

          // Refresh cart count
          const cartRes  = await fetch('/cart.js');
          const cartData = await cartRes.json();

          document.querySelectorAll(
            '[data-cart-count], .cart-count-bubble span[aria-hidden="true"], .cart-count-bubble span'
          ).forEach(el => { el.textContent = cartData.item_count; });

          document.querySelectorAll('.cart-count-bubble').forEach(el => {
            el.removeAttribute('hidden');
            el.classList.remove('hidden');
          });

          // Fire cart events for theme cart drawer / mini-cart
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cartData } }));
          document.dispatchEvent(new CustomEvent('cart:add',     { bubbles: true, detail: { item, cart: cartData } }));

          btn.innerHTML = '✓ Added!';
          setTimeout(() => { btn.innerHTML = orig; btn.disabled = false; }, 1800);

        } catch (err) {
          console.error('[AMZ] ATC error:', err);
          alert(err.message || 'Could not add to cart. Please try again.');
          btn.innerHTML = orig;
          btn.disabled  = false;
        }
      });
    });

    /* ════════════════════════════
       6. BUY NOW
       Also selling_plan aware — reads full FormData.
    ════════════════════════════ */
    page.querySelectorAll('.js-buynow').forEach(btn => {
      btn.addEventListener('click', async () => {
        const form = document.getElementById(btn.dataset.form);
        if (!form) return;

        const orig = btn.innerHTML;
        btn.textContent = 'Processing…';
        btn.disabled    = true;

        try {
          const payload = buildCartPayload(form);

          // Clear cart first, then add with selling_plan
          await fetch('/cart/clear.js', { method: 'POST' });

          const res = await fetch('/cart/add.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body:    JSON.stringify(payload)
          });

          if (!res.ok) {
            const err = await res.json();
            throw new Error(err.description || 'Could not process order');
          }

          window.location.href = '/checkout';

        } catch (err) {
          console.error('[AMZ] Buy Now error:', err);
          alert(err.message || 'Something went wrong. Please try again.');
          btn.innerHTML = orig;
          btn.disabled  = false;
        }
      });
    });

    /* ════════════════════════════
       7. SELLING PLAN CHANGE LISTENER
       When user picks a subscription frequency in the app widget,
       update the price display to show the discounted price.
    ════════════════════════════ */
    const form = variantInput?.closest('form');
    if (form) {
      form.addEventListener('change', (e) => {
        const target = e.target;

        // Subscription app changed the selling_plan input
        if (target.name === 'selling_plan' || target.closest('[data-selling-plan]')) {
          const planId = form.querySelector('[name="selling_plan"]')?.value;

          if (planId && planId !== '' && planId !== '0') {
            // Find the selling plan in product data and show discounted price
            const currentVariant = variants.find(v => String(v.id) === String(variantInput?.value));
            if (currentVariant && product.selling_plan_groups) {
              let discountedPrice = null;

              product.selling_plan_groups.forEach(group => {
                group.selling_plans.forEach(plan => {
                  if (String(plan.id) === String(planId)) {
                    plan.price_adjustments.forEach(adj => {
                      if (adj.value_type === 'percentage') {
                        discountedPrice = currentVariant.price * (1 - adj.value / 100);
                      } else if (adj.value_type === 'fixed_amount') {
                        discountedPrice = currentVariant.price - adj.value;
                      } else if (adj.value_type === 'price') {
                        discountedPrice = adj.value;
                      }
                    });
                  }
                });
              });

              if (discountedPrice !== null && discountedPrice > 0) {
                const discStr = formatMoney(Math.round(discountedPrice));
                if (bbPrice)   bbPrice.textContent  = discStr;
                if (col2Price) col2Price.textContent = discStr;
                if (mobilePx)  mobilePx.textContent  = discStr;
              }
            }
          } else {
            // Switched back to one-time — restore original price
            const currentVariant = variants.find(v => String(v.id) === String(variantInput?.value));
            if (currentVariant) {
              const orig = formatMoney(currentVariant.price);
              if (bbPrice)   bbPrice.textContent  = orig;
              if (col2Price) col2Price.textContent = orig;
              if (mobilePx)  mobilePx.textContent  = orig;
            }
          }
        }
      });
    }

    /* ════════════════════════════
       8. MOBILE STICKY BAR
    ════════════════════════════ */
    if (mobileBar && buybox && window.innerWidth <= 640) {
      mobileBar.setAttribute('aria-hidden', 'false');

      mobileBar.querySelector('.js-mobile-cart')?.addEventListener('click', () => {
        const formId = mobileBar.querySelector('.js-mobile-cart').dataset.form;
        document.getElementById(formId)?.querySelector('[name="add"]')?.click();
      });

      new IntersectionObserver(([entry]) => {
        mobileBar.classList.toggle('is-visible', !entry.isIntersecting);
      }, { threshold: 0 }).observe(buybox);
    }

  });
})();